<template>
  <form class="vl-form" @submit.prevent="onSubmit">
    <vl-grid v-vl-spacer:bottom.medium class="gecko-test-form__grid">
      <vl-column width="12" width-s="12" width-xs="12">
        <vl-form-column v-vl-spacer:bottom.small>
          <div class="vl-form__label">Entiteit</div>
          <div>
            <label> {{ deliveryEntityLabel }}</label>
          </div>
        </vl-form-column>
      </vl-column>
      <vl-column v-if="readonly" width="12" width-s="12" width-xs="12">
        <vl-form-column v-vl-spacer:bottom.small>
          <div class="vl-form__label">{{ t('gecko-test-form.testType') }}</div>
          <div>
            <label> {{ testTypeLabel(values.testType) }}</label>
          </div>
        </vl-form-column>
        <FieldArray name="testParameters">
          <vl-form-column v-for="param in values.testParameters" :key="param.key" v-vl-spacer:bottom.small>
            <div class="vl-form__label">{{ param.label }}</div>
            <div>
              <label v-if="param.dataType === GeckoStandardTestParameterDataType.AttributesList">
                {{ attributeLabel(param.value as EntityAttributeDetailDto) }}
              </label>
              <label v-else>{{ param.value }}</label>
            </div>
          </vl-form-column>
        </FieldArray>
      </vl-column>
      <vl-column v-else width="12" width-s="12" width-xs="12">
        <vl-form-column v-vl-spacer:bottom.small>
          <PbsSelectField
            name="testType"
            :label="t('gecko-test-form.testType')"
            :placeholder="$t('gecko-test-form.testType')"
            :options="allowedTestTypes"
            :custom-label="testTypeLabel"
            :mod-disabled="!!testToEdit"
          />
        </vl-form-column>
        <FieldArray name="testParameters">
          <vl-form-column v-for="(param, idx) in values.testParameters" :key="param.key" v-vl-spacer:bottom.small>
            <PbsSelectField
              v-if="param.dataType === GeckoStandardTestParameterDataType.AttributesList"
              :name="`testParameters[${idx}].value`"
              :label="param.label"
              :placeholder="param.label"
              :options="entity.model?.attributes || []"
              :custom-label="attributeLabel"
            />
            <PbsInputField
              v-else
              :name="`testParameters[${idx}].value`"
              :label="param.label"
              :placeholder="param.label"
            />
          </vl-form-column>
        </FieldArray>
      </vl-column>
      <vl-column width="12" width-s="12" width-xs="12" class="gecko-test-form__actions">
        <vl-action-group v-vl-spacer:top.medium>
          <vl-button v-if="!readonly" :mod-disabled="data.isSaving" :mod-loading="data.isSaving">
            {{ isEditing ? $t('general.edit') : $t('general.add') }}
          </vl-button>
          <vl-button
            v-focus
            :mod-disabled="data.isSaving"
            mod-icon-before
            mod-secondary
            tag-name="a"
            @click.prevent="closeModal"
          >
            {{ $t('general.cancel') }}
          </vl-button>
        </vl-action-group>
      </vl-column>
    </vl-grid>
  </form>
</template>
<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import { useForm, FieldArray } from 'vee-validate';
import { computed, onMounted, reactive, watch } from 'vue';
import {
  DeliveryEntityDetailDto,
  EntityAttributeDetailDto,
  GeckoStandardTestParameterDataType,
  GeckoStandardTestTypeDto,
  GeckoTestDto
} from '@/api/backoffice/clients';
import { useModalStore } from '@/modules/core/store/modal.store';
import PbsInputField from '@/components/forms/PbsInputField.vue';
import PbsSelectField from '@/components/forms/PbsSelectField.vue';
import { createValidationSchema } from './GeckoTestStandardTests.validations';

const { t } = useI18n();

export type TestTypeDto = {
  id: number;
  name: string;
};

export type GeckoStandardTestFormDto = {
  isEditing: boolean;
  testType: TestTypeDto;
  entity: DeliveryEntityDetailDto;
  parameters: {
    key: string;
    value: string;
  }[];
};

type TestParameter = {
  key: string;
  dataType: GeckoStandardTestParameterDataType;
  label: string;
  value: string | EntityAttributeDetailDto | null;
};

type FormValues = {
  testType: TestTypeDto;
  testParameters: TestParameter[];
};

const props = defineProps<{
  entity: DeliveryEntityDetailDto;
  testToEdit?: GeckoTestDto;
  standardTestTypes: GeckoStandardTestTypeDto[];
  readonly?: boolean;
  onFormSubmit?: (dto: GeckoStandardTestFormDto) => Promise<boolean>;
}>();

const data = reactive({
  isSaving: false
});

const validationSchema = createValidationSchema(t, { standardTestTypes: props.standardTestTypes });

const { handleSubmit, values, setFieldValue } = useForm<FormValues>({
  validationSchema
});

const allowedTestTypes = computed((): TestTypeDto[] => {
  return props.standardTestTypes.filter(isTestAllowed).map((x) => ({
    id: x.id,
    name: x.name
  }));
});

const isEditing = computed(() => !!props.testToEdit);

const deliveryEntityLabel = computed(() => {
  return `${props.entity.code} - ${props.entity.label}`;
});

const entityTypeName = computed(() => {
  return props.entity?.model.entityTypeName;
});

const isTestAllowed = (testType: GeckoStandardTestTypeDto) => {
  if (!entityTypeName.value) {
    return false;
  }
  return testType.entityTypes.includes(entityTypeName.value);
};

const getParameterLabel = (testType: GeckoStandardTestTypeDto, parameterName: string) => {
  const parameterType = testType?.parameters?.find((p) => p.name === parameterName);
  if (!parameterType) {
    return parameterName;
  }

  if (parameterType.label) {
    return parameterType.label;
  }
  return t(`gecko-test-form.parameter.${parameterType.name}`);
};

const getParameterValue = (testType: GeckoStandardTestTypeDto, parameterName: string): string => {
  const parameterType = testType?.parameters?.find((p) => p.name === parameterName);
  const defaultValue = parameterType?.defaultValue || '';

  const parameterKeys = Object.keys(props.testToEdit?.test.parameters || {});
  const actualKey = parameterKeys.find((key) => key.toLowerCase() === parameterName.toLowerCase());
  const value = actualKey ? props.testToEdit.test.parameters[actualKey] : '';
  return value || defaultValue;
};

const testTypeLabel = (type: TestTypeDto) => {
  if (!type) {
    return '';
  }
  return `${type.id} (${type.name})`;
};

const attributeLabel = (attr: EntityAttributeDetailDto) => {
  if (!attr) return '';
  return attr.label ? `${attr.code} - ${attr.label}` : attr.code || '';
};

watch(
  () => values.testType,
  (newValue) => setTestParameters(newValue)
);

onMounted(() => {
  if (props.testToEdit) {
    loadTestToEdit(props.testToEdit);
  }
});

const loadTestToEdit = (testToEdit: GeckoTestDto) => {
  const id = parseInt(testToEdit.test.code?.split('_')[0], 10);
  const standardTestType = props.standardTestTypes.find((x) => x.id === id);

  if (standardTestType) {
    const testType = { id: standardTestType.id, name: standardTestType.name };
    setFieldValue('testType', testType);
  }
};

const setTestParameters = (newTestType: TestTypeDto) => {
  const testType = props.standardTestTypes.find((x) => x.id === newTestType?.id);

  const parameters =
    testType?.parameters?.map((p) => {
      const rawValue = getParameterValue(testType, p.name);
      let value: string | EntityAttributeDetailDto | null = rawValue;

      if (p.dataType === GeckoStandardTestParameterDataType.AttributesList) {
        value = props.entity.model?.attributes?.find((a) => a.code === rawValue) ?? null;
      }

      return {
        key: p.name,
        dataType: p.dataType,
        label: getParameterLabel(testType, p.name),
        value
      };
    }) || [];

  setFieldValue('testParameters', parameters);
};

const closeModal = () => {
  useModalStore().hideModal();
};

const onSuccess = async () => {
  const standardTestType = props.standardTestTypes.find((x) => x.id === values.testType.id);
  const testType: TestTypeDto = { id: standardTestType.id, name: standardTestType.name };
  const parameters =
    values.testParameters?.map((x) => ({
      key: x.key,
      value:
        x.dataType === GeckoStandardTestParameterDataType.AttributesList
          ? ((x.value as EntityAttributeDetailDto)?.code ?? '')
          : (x.value as string)
    })) || [];

  const dto: GeckoStandardTestFormDto = {
    isEditing: !!props.testToEdit,
    testType,
    entity: props.entity,
    parameters
  };

  const success = await props.onFormSubmit?.(dto);
  if (success) {
    closeModal();
  }
};

const onInvalidSubmit = () => {};

const onSubmit = handleSubmit(onSuccess, onInvalidSubmit);
</script>

<style lang="scss" scoped>
.gecko-test-form__grid {
  min-height: 300px;
  display: flex;
  flex-direction: column;
}

.gecko-test-form__actions {
  flex: 0 0 auto; /* button row does not grow */
  margin-top: auto; /* push to bottom */
}
</style>
